// =============================================================================
// API client — thin fetch wrapper for the GAIIFF_CMS backend
// =============================================================================

import type {
  AssetCatalogEntry,
  AssetListResponse,
  BrandListResponse,
  CacLtvResponse,
  ChannelPerformanceResponse,
  CreateAssetInput,
  GenerateAssetInput,
  MetaPagesResponse,
  MirrorResponse,
  Ratio,
} from "./types";

const DEFAULT_BASE_URL =
  "https://ujeanlvqlnclag3jbdnel6f5rq0hpiug.lambda-url.us-east-1.on.aws";

/** Resolved base URL: env var takes precedence, else the deployed Function URL. */
export const API_BASE_URL: string = (
  import.meta.env.VITE_API_BASE_URL ?? DEFAULT_BASE_URL
).replace(/\/+$/, "");

/** S3 console URL for the Asset Store bucket (where mirrored binaries live). */
const DEFAULT_ASSET_BUCKET_URL =
  "https://s3.console.aws.amazon.com/s3/buckets/canvabackendstack-assetstorebucket831d274a-heuxpvemy4br?region=us-east-1";

/** Asset Store bucket link: env override, else the deployed bucket's console URL. */
export const ASSET_BUCKET_URL: string =
  import.meta.env.VITE_ASSET_BUCKET_URL ?? DEFAULT_ASSET_BUCKET_URL;

/** Error carrying the HTTP status and any server-provided message. */
export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Normalizes the backend Ratio union to a number or `null` (no value). */
export function ratioValue(ratio: Ratio | undefined | null): number | null {
  if (!ratio) return null;
  return typeof ratio.value === "number" && Number.isFinite(ratio.value)
    ? ratio.value
    : null;
}

/** Human-readable reason a ratio has no value, if provided. */
export function ratioReason(ratio: Ratio | undefined | null): string | null {
  if (!ratio) return null;
  return typeof ratio.reason === "string" ? ratio.reason : null;
}

/** Provider that returns the current bearer token (or null), possibly async. */
export type TokenProvider = () => string | null | Promise<string | null>;

async function request<T>(
  path: string,
  getToken: TokenProvider,
  init?: RequestInit,
): Promise<T> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (init?.body) headers.set("Content-Type", "application/json");
  const token = await getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  } catch (err) {
    // Network / CORS failure — surface a friendly message.
    throw new ApiError(0, err instanceof Error ? err.message : "Network error");
  }

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const message =
      (parsed && typeof parsed === "object" && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : undefined) ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message);
  }

  return parsed as T;
}

/** Factory: build a client bound to a token provider. */
export function createApiClient(getToken: TokenProvider) {
  return {
    listBrands(signal?: AbortSignal): Promise<BrandListResponse> {
      return request<BrandListResponse>(`/api/brands`, getToken, { signal });
    },
    getCacLtv(brandId: string, signal?: AbortSignal): Promise<CacLtvResponse> {
      return request<CacLtvResponse>(
        `/api/analytics/cac-ltv?brandId=${encodeURIComponent(brandId)}`,
        getToken,
        { signal },
      );
    },
    getChannelPerformance(
      brandId: string,
      signal?: AbortSignal,
    ): Promise<ChannelPerformanceResponse> {
      return request<ChannelPerformanceResponse>(
        `/api/analytics/channel-performance?brandId=${encodeURIComponent(brandId)}`,
        getToken,
        { signal },
      );
    },
    listAssets(
      opts?: { brandId?: string; sourcePlatform?: string },
      signal?: AbortSignal,
    ): Promise<AssetListResponse> {
      const params = new URLSearchParams();
      if (opts?.brandId) params.set("brandId", opts.brandId);
      if (opts?.sourcePlatform)
        params.set("sourcePlatform", opts.sourcePlatform);
      const qs = params.toString();
      return request<AssetListResponse>(
        `/api/assets${qs ? `?${qs}` : ""}`,
        getToken,
        { signal },
      );
    },
    createAsset(input: CreateAssetInput): Promise<AssetCatalogEntry> {
      return request<AssetCatalogEntry>(`/api/assets`, getToken, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    generateAsset(input: GenerateAssetInput): Promise<AssetCatalogEntry> {
      return request<AssetCatalogEntry>(`/api/assets/generate`, getToken, {
        method: "POST",
        body: JSON.stringify(input),
      });
    },
    approveAsset(id: string): Promise<AssetCatalogEntry> {
      return request<AssetCatalogEntry>(
        `/api/assets/${encodeURIComponent(id)}/approve`,
        getToken,
        { method: "POST" },
      );
    },
    publishAsset(
      id: string,
      caption: string,
      pageId: string,
    ): Promise<AssetCatalogEntry> {
      return request<AssetCatalogEntry>(
        `/api/assets/${encodeURIComponent(id)}/publish`,
        getToken,
        { method: "POST", body: JSON.stringify({ caption, pageId }) },
      );
    },
    listMetaPages(signal?: AbortSignal): Promise<MetaPagesResponse> {
      return request<MetaPagesResponse>(`/api/assets/meta/pages`, getToken, {
        signal,
      });
    },
    /**
     * Fetch a stored asset's bytes through the authenticated proxy and return a
     * blob object URL for display (an <img src> cannot send the bearer token).
     * Caller is responsible for URL.revokeObjectURL when done.
     */
    async assetObjectUrl(id: string): Promise<string> {
      const headers = new Headers({ Accept: "image/*" });
      const token = await getToken();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch(
        `${API_BASE_URL}/api/assets/${encodeURIComponent(id)}/asset`,
        { headers },
      );
      if (!res.ok) {
        throw new ApiError(res.status, `Failed to load asset (${res.status})`);
      }
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    },
    mirrorAsset(id: string): Promise<MirrorResponse> {
      return request<MirrorResponse>(
        `/api/assets/${encodeURIComponent(id)}/mirror`,
        getToken,
        { method: "POST", body: JSON.stringify({ presign: true }) },
      );
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
